// "YOUR CONTACT INFO" — THE APP'S CONTACT SHEET, OPENED FROM REVIEW & EXPORT (WEB-PARITY-5, ruling D3; YU-7;
// OwnerProfileEditor.swift:7-129, presented by ReviewView.swift:269-271 from "Add your contact info" and the export
// alert's "Add contact info").
//
// The app's four sections, in its order and words: "Name on the letter" (Full name), "Find your address" (Apple's
// suggestions, which fill the fields below), "Mailing address" (Mailing street, City, State, ZIP) and "Contact" (Email,
// Phone), each with its footer, and "Save". Every field is optional, as the app's are; the letterhead prints once there
// is a name and a street (`OwnerProfile.isMinimallyComplete`).
//
// SAVED TO THIS TAB, NOT TO THE SERVER AND NOT TO THE BROWSER'S STORAGE (`../../app/contact.ts`): the website keeps no
// profile. The PDF and the emailed copy carry it as the letterhead; a letter request carries it as the owner.
//
// "Cancel" is the app's own word (ReviewView.swift:312). The app's sheet closes by a swipe; a web dialog needs a way out
// that is not Save, and Escape and Cancel are it.

import { useEffect, useId, useRef, useState } from "react";
import { EMPTY_CONTACT, fieldsOfOneLine, holdContact, type HeldContact } from "../../app/contact";
import { phoneAsTyped } from "../../app/phone";
import { useSession } from "../../app/session";
import { AddressCombobox } from "../../components/AddressCombobox";
import { word } from "./reviewWording";
import { sayG, type GenerateWordingId } from "./generateWording";

function Field({
  labelId, value, onChange, type = "text", autoComplete, inputMode,
}: {
  labelId: GenerateWordingId;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "tel";
  autoComplete: string;
  inputMode?: "text" | "email" | "tel" | "numeric";
}) {
  return (
    <label className="field contact-field">
      <span data-held-copy={labelId}>{sayG(labelId)}</span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </label>
  );
}

function Section({ headerId, footerId, children }: { headerId: GenerateWordingId; footerId: GenerateWordingId; children: React.ReactNode }) {
  return (
    <fieldset className="contact-section">
      <legend data-held-copy={headerId}>{sayG(headerId)}</legend>
      {children}
      <p className="contact-footer" data-held-copy={footerId}>{sayG(footerId)}</p>
    </fieldset>
  );
}

export function ContactSheet({ start, onClose }: { start: HeldContact | null; onClose: () => void }) {
  const session = useSession();
  const [draft, setDraft] = useState<HeldContact>(start ?? EMPTY_CONTACT);
  const [find, setFind] = useState("");
  const headingId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const set = (key: keyof HeldContact) => (v: string) => setDraft((d) => ({ ...d, [key]: v }));

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => before?.focus?.();
  }, []);

  return (
    <div className="alert-backdrop">
      <div
        ref={dialog}
        tabIndex={-1}
        className="app-sheet rv-sheet contact-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-sheet="contact"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <div className="app-sheet-bar">
          <button type="button" className="btn quiet" data-held-copy="review.export.cancel" onClick={onClose}>
            {word("review.export.cancel")}
          </button>
          <h2 id={headingId} data-held-copy="contact.title">{sayG("contact.title")}</h2>
          <button
            type="button"
            className="btn quiet strong"
            data-held-copy="contact.save"
            onClick={() => {
              holdContact(draft);
              onClose();
            }}
          >
            {sayG("contact.save")}
          </button>
        </div>
        <div className="app-sheet-body contact-body">
          <Section headerId="contact.name.header" footerId="contact.name.footer">
            {/* The section's header names the box, as the app's Form section does; "Full name" is its placeholder. */}
            <label className="field contact-field">
              <span className="visually-hidden">{sayG("contact.name.header")}</span>
              <input
                type="text"
                value={draft.name}
                placeholder={sayG("contact.name.placeholder")}
                data-held-copy="contact.name.placeholder"
                autoComplete="name"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => set("name")(e.currentTarget.value)}
              />
            </label>
          </Section>

          <Section headerId="contact.find.header" footerId="contact.find.footer">
            <AddressCombobox
              suggester={session.addressSuggester}
              label={sayG("contact.find.header")}
              placeholder={sayG("contact.find.placeholder")}
              value={find}
              onChange={setFind}
              onPick={(line) => {
                // `applyCompletion` (OwnerProfileEditor.swift:116-128): the pick fills the fields, and the search clears.
                setDraft((d) => ({ ...d, ...fieldsOfOneLine(line) }));
                setFind("");
              }}
            />
          </Section>

          <Section headerId="contact.mailing.header" footerId="contact.mailing.footer">
            <Field labelId="contact.street" value={draft.mailingAddress} onChange={set("mailingAddress")} autoComplete="address-line1" />
            <Field labelId="contact.city" value={draft.city} onChange={set("city")} autoComplete="address-level2" />
            <div className="contact-pair">
              <Field labelId="contact.state" value={draft.state} onChange={set("state")} autoComplete="address-level1" />
              <Field labelId="contact.zip" value={draft.zip} onChange={set("zip")} autoComplete="postal-code" inputMode="numeric" />
            </div>
          </Section>

          <Section headerId="contact.reach.header" footerId="contact.reach.footer">
            <Field labelId="contact.email" value={draft.email} onChange={set("email")} type="email" autoComplete="email" inputMode="email" />
            {/* WALK-FIX-1: shown as the letter will print it while the owner types (../../app/phone.ts); never refused. */}
            <Field labelId="contact.phone" value={draft.phone} onChange={(v) => set("phone")(phoneAsTyped(v))} type="tel" autoComplete="tel" inputMode="tel" />
          </Section>
        </div>
      </div>
    </div>
  );
}
